import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { API } from "@/App";
import PharmeasyLayout from "@/components/PharmeasyLayout";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { FileText, ChevronLeft, ChevronRight, CheckCircle, XCircle, Clock, Eye, Copy, ExternalLink } from "lucide-react";
import { toast } from "sonner";

const PharmeasyApiLogs = ({ user, onLogout }) => {
  const [logs, setLogs] = useState([]);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [selectedLog, setSelectedLog] = useState(null);
  const pageSize = 50;

  const fetchLogs = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/pharmeasy-orders/api-logs?page=${page}&size=${pageSize}`);
      setLogs(res.data.data);
      setTotal(res.data.total);
      setTotalPages(res.data.total_pages);
    } catch (e) { /* silent */ }
    finally { setLoading(false); }
  }, [page]);

  useEffect(() => { fetchLogs(); }, [fetchLogs]);

  const formatDate = (iso) => {
    if (!iso) return "—";
    try { return new Date(iso).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "medium" }); }
    catch { return iso; }
  };

  const copyToClipboard = (data, label) => {
    navigator.clipboard.writeText(JSON.stringify(data, null, 2));
    toast.success(`${label} copied to clipboard`);
  };

  const getApiTypeBadge = (apiType) => {
    const types = {
      "unassigned_fetch": { bg: "bg-emerald-100", text: "text-emerald-700", label: "Unassigned" },
      "detail_fetch": { bg: "bg-blue-100", text: "text-blue-700", label: "Detail" },
      "events_fetch": { bg: "bg-purple-100", text: "text-purple-700", label: "Events" },
      "riders_fetch": { bg: "bg-orange-100", text: "text-orange-700", label: "Riders" },
      "trips_fetch": { bg: "bg-teal-100", text: "text-teal-700", label: "Trips" },
      "trip_tasks_fetch": { bg: "bg-cyan-100", text: "text-cyan-700", label: "Trip Tasks" },
      "trip_status_sync": { bg: "bg-indigo-100", text: "text-indigo-700", label: "Trip Status" },
      "google_maps": { bg: "bg-red-100", text: "text-red-700", label: "Google Maps" },
      "google_maps_geocode": { bg: "bg-red-100", text: "text-red-700", label: "Geocode" },
      "google_maps_distance": { bg: "bg-rose-100", text: "text-rose-700", label: "Distance Matrix" },
    };
    const style = types[apiType] || { bg: "bg-slate-100", text: "text-slate-700", label: apiType || "Unknown" };
    return <Badge className={`${style.bg} ${style.text} text-xs`}>{style.label}</Badge>;
  };

  return (
    <PharmeasyLayout user={user} onLogout={onLogout}>
      <div className="space-y-6" data-testid="pharmeasy-api-logs">
        <div>
          <h1 className="text-4xl font-bold text-slate-800 mb-2">API Logs</h1>
          <p className="text-slate-600">Log of all external API calls made to Pharmeasy BOLT</p>
        </div>

        <Card className="border-0 shadow-lg">
          <CardContent className="p-0">
            {loading ? (
              <div className="p-8 text-center text-slate-600">Loading logs...</div>
            ) : logs.length === 0 ? (
              <div className="p-12 text-center">
                <FileText className="w-16 h-16 text-slate-300 mx-auto mb-4" />
                <p className="text-lg font-medium text-slate-500">No API logs yet</p>
                <p className="text-sm text-slate-400 mt-1">Logs will appear after you fetch orders</p>
              </div>
            ) : (
              <>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-slate-50 border-b">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-slate-700 whitespace-nowrap">Timestamp</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-slate-700 whitespace-nowrap">Cron Name</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-slate-700 whitespace-nowrap">Method</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-slate-700 whitespace-nowrap">URL</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-slate-700 whitespace-nowrap">Status</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-slate-700 whitespace-nowrap">Response</th>
                        <th className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wider text-slate-700 whitespace-nowrap">Duration</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-slate-700 whitespace-nowrap">Triggered By</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-slate-700 whitespace-nowrap">Page</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-slate-700 whitespace-nowrap">Error</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-slate-700 whitespace-nowrap w-10"></th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-200">
                      {logs.map((log) => (
                        <tr 
                          key={log._id} 
                          className="hover:bg-slate-50 cursor-pointer transition-colors" 
                          data-testid={`api-log-row-${log._id}`}
                          onClick={() => setSelectedLog(log)}
                        >
                          <td className="px-4 py-3 align-middle text-sm text-slate-600 whitespace-nowrap">
                            <div>{formatDate(log.timestamp).split(", ")[0]}</div>
                            <div className="text-xs text-slate-500">{formatDate(log.timestamp).split(", ")[1]}</div>
                          </td>
                          <td className="px-4 py-3 align-middle">{getApiTypeBadge(log.api_type)}</td>
                          <td className="px-4 py-3 align-middle">
                            <Badge variant="outline" className="text-xs font-mono">{log.method}</Badge>
                          </td>
                          <td className="px-4 py-3 align-middle text-sm font-mono text-slate-500 max-w-[200px] truncate" title={log.url}>
                            {log.url?.replace("https://flash.boltonline.in", "") || "—"}
                          </td>
                          <td className="px-4 py-3 align-middle">
                            {log.response_status ? (
                              <Badge className={log.response_status >= 200 && log.response_status < 300 ? "bg-emerald-100 text-emerald-800" : "bg-red-100 text-red-800"}>
                                {log.response_status >= 200 && log.response_status < 300
                                  ? <CheckCircle className="w-3 h-3 mr-1" />
                                  : <XCircle className="w-3 h-3 mr-1" />}
                                {log.response_status}
                              </Badge>
                            ) : <span className="text-xs text-slate-400">—</span>}
                          </td>
                          <td className="px-4 py-3 align-middle text-sm text-slate-600">
                            {log.response_summary ? (
                              <span>{log.response_summary.data_count ?? log.response_summary.events_count ?? log.response_summary.riders_count ?? 0} records{log.response_summary.hasNext ? " (more)" : ""}</span>
                            ) : "—"}
                          </td>
                          <td className="px-4 py-3 align-middle text-right">
                            <span className="text-sm font-mono text-slate-600 flex items-center justify-end gap-1">
                              <Clock className="w-3 h-3 text-slate-400" />
                              {log.duration_ms ?? "—"} ms
                            </span>
                          </td>
                          <td className="px-4 py-3 align-middle text-sm text-slate-600">{log.triggered_by || "—"}</td>
                          <td className="px-4 py-3 align-middle text-sm font-mono text-slate-500">{log.request_body?.page || "—"}</td>
                          <td className="px-4 py-3 align-middle text-sm text-red-500 max-w-[150px] truncate" title={log.error || ""}>
                            {log.error || "—"}
                          </td>
                          <td className="px-4 py-3 align-middle">
                            <Eye className="w-4 h-4 text-slate-400" />
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                {totalPages > 1 && (
                  <div className="flex items-center justify-between px-4 py-3 border-t border-slate-100">
                    <p className="text-sm text-slate-500">Showing {(page - 1) * pageSize + 1}–{Math.min(page * pageSize, total)} of {total}</p>
                    <div className="flex gap-2">
                      <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage(page - 1)}>
                        <ChevronLeft className="w-4 h-4" />
                      </Button>
                      <span className="flex items-center px-3 text-sm text-slate-600">Page {page} of {totalPages}</span>
                      <Button variant="outline" size="sm" disabled={page >= totalPages} onClick={() => setPage(page + 1)}>
                        <ChevronRight className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                )}
              </>
            )}
          </CardContent>
        </Card>
      </div>

      {/* API Log Detail Modal */}
      <Dialog open={!!selectedLog} onOpenChange={() => setSelectedLog(null)}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center justify-between">
              <span className="flex items-center gap-2">
                API Log Details
                {selectedLog?.api_type && getApiTypeBadge(selectedLog.api_type)}
              </span>
              {selectedLog?.response_status && (
                <Badge className={selectedLog.response_status >= 200 && selectedLog.response_status < 300 ? "bg-emerald-100 text-emerald-800" : "bg-red-100 text-red-800"}>
                  {selectedLog.response_status >= 200 && selectedLog.response_status < 300
                    ? <CheckCircle className="w-3 h-3 mr-1" />
                    : <XCircle className="w-3 h-3 mr-1" />}
                  {selectedLog.response_status}
                </Badge>
              )}
            </DialogTitle>
          </DialogHeader>

          {selectedLog && (
            <div className="space-y-4">
              {/* Basic Info */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="bg-slate-50 p-4 rounded-lg">
                  <p className="text-xs text-slate-500">Method</p>
                  <p className="font-mono font-bold">{selectedLog.method}</p>
                </div>
                <div className="bg-slate-50 p-4 rounded-lg">
                  <p className="text-xs text-slate-500">Duration</p>
                  <p className="font-mono font-bold">{selectedLog.duration_ms ?? "—"} ms</p>
                </div>
                <div className="bg-slate-50 p-4 rounded-lg">
                  <p className="text-xs text-slate-500">Triggered By</p>
                  <p className="font-medium text-sm truncate">{selectedLog.triggered_by || "—"}</p>
                </div>
                <div className="bg-slate-50 p-4 rounded-lg">
                  <p className="text-xs text-slate-500">Timestamp</p>
                  <p className="text-sm">{formatDate(selectedLog.timestamp)}</p>
                </div>
              </div>

              {/* URL */}
              <div className="bg-slate-50 p-4 rounded-lg">
                <div className="flex items-center justify-between mb-1">
                  <p className="text-xs text-slate-500 font-semibold">URL</p>
                  <Button variant="ghost" size="sm" onClick={() => copyToClipboard(selectedLog.url, "URL")}>
                    <Copy className="w-3 h-3 mr-1" /> Copy
                  </Button>
                </div>
                <p className="font-mono text-sm break-all text-blue-600">{selectedLog.url}</p>
              </div>

              {/* Error (if any) */}
              {selectedLog.error && (
                <div className="bg-red-50 border border-red-200 p-4 rounded-lg">
                  <p className="text-xs text-red-600 font-semibold mb-1">Error</p>
                  <p className="font-mono text-sm text-red-700">{selectedLog.error}</p>
                </div>
              )}

              {/* Request Headers */}
              {selectedLog.request_headers && Object.keys(selectedLog.request_headers).length > 0 && (
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="font-semibold text-slate-800">Request Headers</h3>
                    <Button variant="ghost" size="sm" onClick={() => copyToClipboard(selectedLog.request_headers, "Request Headers")}>
                      <Copy className="w-3 h-3 mr-1" /> Copy
                    </Button>
                  </div>
                  <div className="bg-slate-900 text-green-400 p-4 rounded-lg overflow-x-auto">
                    <pre className="font-mono text-xs whitespace-pre-wrap">
                      {JSON.stringify(selectedLog.request_headers, null, 2)}
                    </pre>
                  </div>
                </div>
              )}

              {/* Request Body */}
              {selectedLog.request_body && Object.keys(selectedLog.request_body).length > 0 && (
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="font-semibold text-slate-800">Request Body</h3>
                    <Button variant="ghost" size="sm" onClick={() => copyToClipboard(selectedLog.request_body, "Request Body")}>
                      <Copy className="w-3 h-3 mr-1" /> Copy
                    </Button>
                  </div>
                  <div className="bg-slate-900 text-yellow-400 p-4 rounded-lg overflow-x-auto">
                    <pre className="font-mono text-xs whitespace-pre-wrap">
                      {JSON.stringify(selectedLog.request_body, null, 2)}
                    </pre>
                  </div>
                </div>
              )}

              {/* Response Summary */}
              {selectedLog.response_summary && Object.keys(selectedLog.response_summary).length > 0 && (
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="font-semibold text-slate-800">Response Summary</h3>
                    <Button variant="ghost" size="sm" onClick={() => copyToClipboard(selectedLog.response_summary, "Response Summary")}>
                      <Copy className="w-3 h-3 mr-1" /> Copy
                    </Button>
                  </div>
                  <div className="bg-slate-900 text-cyan-400 p-4 rounded-lg overflow-x-auto">
                    <pre className="font-mono text-xs whitespace-pre-wrap">
                      {JSON.stringify(selectedLog.response_summary, null, 2)}
                    </pre>
                  </div>
                </div>
              )}

              {/* Full Log ID */}
              <div className="text-xs text-slate-400 pt-2 border-t">
                Log ID: <span className="font-mono">{selectedLog._id}</span>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </PharmeasyLayout>
  );
};

export default PharmeasyApiLogs;
